import { useNavigate } from "react-router-dom";
import { Timer, Coffee, Fuel, Clock } from "lucide-react";
import { toast } from "sonner";
import { useTacho } from "@/context/TachoContext";
import { api, fmtClock, fmtDur, formatErr } from "@/lib/api";
import { BigButton } from "@/components/BigButton";
import { t } from "@/i18n";

const LEVEL = {
  ok: { color: "#4accff", text: "Puoi guidare" },
  warning: { color: "#f59e0b", text: "Pianifica la pausa" },
  critical: { color: "#ef0298", text: "Fermati presto" },
  exceeded: { color: "#ef0298", text: "Tempo di guida esaurito" },
};

export const TachoWidget = ({ compact = false }) => {
  const { tacho, refresh } = useTacho();
  const nav = useNavigate();
  if (!tacho) return <div className="nv-card h-40 animate-pulse" />;
  const lv = LEVEL[tacho.level];
  const pct = Math.min(100, (1 - tacho.continuous_remaining_sec / tacho.continuous_limit_sec) * 100);

  return (
    <section className="nv-card p-5 sm:p-6 relative overflow-hidden" data-testid="tacho-widget" style={{ borderColor: `${lv.color}66` }}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="nv-eyebrow flex items-center gap-2"><Timer className="h-4 w-4" /> {t("Ore di guida prima della pausa")}</p>
          <p className="font-mono text-5xl sm:text-6xl font-bold mt-2 tabular-nums" style={{ color: lv.color }} data-testid="tacho-remaining">
            {fmtClock(tacho.remaining_sec)}
          </p>
          <p className="mt-1 font-display text-xl uppercase font-bold tracking-wide" style={{ color: lv.color }} data-testid="tacho-level">
            {tacho.on_break ? t("In pausa") : t(lv.text)}
          </p>
        </div>
        <div className="text-right text-sm text-slate-300 space-y-1">
          <p>{t("Guida continua:")} <b className="text-white font-mono">{fmtDur(tacho.continuous_driven_sec)}</b> / 4h 30m</p>
          <p>{t("Guida oggi rimasta:")} <b className="text-white font-mono" data-testid="tacho-daily">{fmtDur(Math.ceil(tacho.daily_remaining_sec / 60) * 60)}</b> / {fmtDur(tacho.daily_limit_sec)}</p>
          <p>{t(tacho.is_driving ? "Cronotachigrafo: GUIDA" : tacho.on_break ? "Cronotachigrafo: RIPOSO" : "Cronotachigrafo: ALTRO LAVORO")}</p>
        </div>
      </div>
      <div className="mt-4 h-4 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full transition-[width] duration-1000" style={{ width: `${pct}%`, background: `linear-gradient(90deg,#4accff,${lv.color})` }} />
      </div>
      {tacho.on_break && (
        <p className="mt-4 text-lg font-semibold flex items-center gap-2" data-testid="tacho-break-info">
          <Coffee className="h-5 w-5 text-[#f59e0b]" />
          {tacho.break_remaining_sec > 0 ? t("Pausa: mancano {t} ai 45 minuti", { t: fmtClock(tacho.break_remaining_sec) }) : t("Pausa di 45 minuti completata: puoi ripartire")}
        </p>
      )}
      {!compact && tacho.level !== "ok" && !tacho.on_break && (
        <div className="mt-4">
          <BigButton testId="tacho-find-break-btn" icon={Fuel} color="#ef0298" label={t("Trova area di sosta")} sub={t("Calcola il percorso verso la stazione di servizio più vicina")} onClick={() => nav("/pausa")} />
        </div>
      )}
      {tacho.extension_active && (
        <p className="mt-3 font-semibold text-[#f59e0b] flex items-center gap-2" data-testid="tacho-ext-active"><Clock className="h-5 w-5" /> {t("10ª ora di guida attiva oggi (straordinario)")}</p>
      )}
      {!compact && !tacho.extension_active && (
        <div className="mt-4">
          <BigButton testId="tacho-extend-btn" icon={Clock} variant="outline" color="#f59e0b" label={t("Attiva la 10ª ora")}
            sub={t("Straordinario: ne restano {n} su 2 questa settimana", { n: tacho.extensions_left })} disabled={!tacho.extensions_left}
            onClick={() => api.post("/tacho/extend").then(refresh).catch((e) => toast.error(formatErr(e)))} />
        </div>
      )}
      {!compact && (
        <button data-testid="tacho-demo-reset" className="mt-3 text-xs text-slate-500 underline" onClick={() => api.post("/tacho/demo-reset").then(refresh)}>
          {t("Ripristina contatore dimostrativo")}
        </button>
      )}
    </section>
  );
};
